import { PortfolioAddOrUpdateModel } from "../../../screens/portfolio-info/types/PortfolioAddOrUpdateModel";
import { PortfolioDto } from "../../../screens/portfolio-info/types/PortfolioDto";
import { usePortfolioCommonService } from "./usePortfolioCommonService";
import { portfolioMappers } from "../../mappers/PortfolioMappers";
import { LocalStorageConstants } from "../../constants/LocalStorageConstants";
import { portfolioCalculationService } from "../../services/portfolio-calculation-service/PortfolioCalculationService";
import { useAuthContext } from "../../../screens/authentication/hooks/use-auth/AuthContext";
import { PortfolioModel } from "../../../screens/portfolio-info/types/PortfolioModel";
import { ParameterType } from "../../../screens/options-table/types/ParameterType";
import { ProfileType } from "../../../screens/options-table/types/ProfileType";
import { useNavigate } from "react-router-dom";
import { TableInfoType } from "../../../screens/options-table/types/TableInfoType";
import {
    OrderForUpdate,
    PositionForUpdate
} from "../../../screens/positions-and-orders/utils/CustomImpliedVolatilityInfoBuilder";
import type { MarketDataDto } from "../../types/MarketDataDto";
import { AllPortfoliosConstants } from "../../../screens/portfolio-info/constants/AllPortfoliosConstants";
import { useMemo } from "react";
import { useSnackbar } from "notistack";
import { createAuthenticatedApiClient } from "../../services/api/AuthenticatedApiClient";
import { createPortfolioApi } from "../../services/api/PortfolioApi";
import { createPortfolioGroupApi } from "../../services/api/PortfolioGroupApi";
import { portfolioModelService } from "../../services/portfolio-model-service/PortfolioModelService";
import { PositionStateFilter } from "../../types/PositionStateFilter";

export function useSelectedPortfolioService() {
    const {
        underlyingAssetDto,
        marketDataDto,
        portfolioGroups,
        setPortfolioGroups,
        selectedPortfolioGroup,
        setSelectedPortfolioGroup,
        setSelectedPortfolioGroupByName,
        createPortfolioGroup,
        editSelectedPortfolioGroup,
        deleteSelectedPortfolioGroup,
        portfolios,
        underlyingAssetPortfolios,
        setUnderlyingAssetPortfolios,
        setPortfolios,
        selectedPortfolio,
        isAllPortfoliosSelected,
        setSelectedPortfolio,
        setIsAllPortfoliosSelected,
        portfolioProfile,
        comparedPortfolioProfile,
        setSelectedPortfolioByName,
        setAllPortfoliosByUnderlyingAsset,
        refreshPortfolioProfile,
        selectedComparedPortfolio,
        setSelectedComparedPortfolioByName,
        refreshComparedPortfolioProfile,
        setComparedPortfolioProfile,
        setSelectedComparedPortfolio,
        selectedPortfolioDates,
    } = usePortfolioCommonService();

    const { userAuthData, isUserAuthenticated } = useAuthContext();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const apiClient = useMemo(
        () => createAuthenticatedApiClient({ userAuthData, isUserAuthenticated, navigate }),
        [userAuthData, isUserAuthenticated, navigate],
    );
    const portfolioApi = useMemo(() => createPortfolioApi(apiClient), [apiClient]);
    const portfolioGroupApi = useMemo(() => createPortfolioGroupApi(apiClient), [apiClient]);
    
    async function createPortfolio(portfolioModel: PortfolioAddOrUpdateModel): Promise<void> {
        if (!marketDataDto || !selectedPortfolioGroup) {
            return;
        }

        const currentBasePrice = marketDataDto.UnderlyingPrice!;
        const chartDeltaInPercent = 0.2;
        const offset = Math.round(currentBasePrice * chartDeltaInPercent);
        const calculationCount = 200;
        const calculationStep =   Math.round(offset * 2 / calculationCount);

        const newPortfolioModel: PortfolioModel = {
            id: 0,
            name: portfolioModel.name,
            description: portfolioModel.description,
            marginBuffer: portfolioModel.marginBuffer,
            inceptionDate: portfolioModel.inceptionDate,
            useValuationDate: portfolioModel.useValuationDate,
            valuationDate: portfolioModel.valuationDate,
            underlyingAsset: portfolioMappers.mapUnderlyingAsset(underlyingAssetDto!),
            portfolioGroup: portfolioMappers.mapPortfolioGroupModelToModel(selectedPortfolioGroup),
            positions: [],
            orders: [],
            portfolioBalanceHistory: [],
            portfolioSettings: {
                useOffset: true,
                offset: offset,
                minUnderlyingPrice: Math.round(currentBasePrice - offset),
                maxUnderlyingPrice: Math.round(currentBasePrice + offset),
                useBalanceRangeRestriction: false,
                minBalance: 0,
                maxBalance: 0,
                calculationStep: calculationStep !== 0 ? calculationStep : 100,
                selectedOptionParameterType: ParameterType.Delta,
                selectedTableInfoType: TableInfoType.Orders,
                selectedProfileTypes: [ProfileType.Current, ProfileType.Custom, ProfileType.Expiration],
                selectedCustomDate: new Date(),
                hideCommitedBalance: false,
                commitedBalance: 0,
                showTradeSessionBalanceLine: false,
                tradeSessionBalance: 0,
                useSessionBalanceAsZero: false,
                showSupportLine: false,
                supportLinePrice: 0,
                showResistanceLine: false,
                resistanceLinePrice: 0,
                useCustomPrice: false,
                customPrice: marketDataDto.UnderlyingPrice ?? 0,
                customPriceStep: 100,
                usePopupForStepChangeSelector: portfolioModel.usePopupForStepChangeSelector,
                useComparedPortfolio: false,
                comparedPortfolioId: null,
                showDeltaNeutralPriceLines: true,
                showPortfolioPerformance: false,
                usePortfolioAnnualisedRoi: false,
                showDeltaValues: true,
                balanceStartValueX: null,
                balanceEndValueX: null,
                balanceStartValueY: null,
                balanceEndValueY: null,
                isTripleCustomBalanceEnabled: false,
                isTripleCustomBalanceUsingEqualShifts: false,
                isCustomImpliedVolatilityEnabled: false,
                positionStateFilter: PositionStateFilter.Open,
                positionsAssetTypeFilter: null,
                positionsTargetPlatformFilter: null,
                includeFeesInBalance: false,
                mergePositionsAcrossPlatforms: false,
                orderStateFilter: null,
                ordersAssetTypeFilter: null,
                ordersTargetPlatformFilter: null,
            },
            modifiedAt: null,
        };

        await savePortfolio(newPortfolioModel);
        
        setPortfolios([...portfolios, newPortfolioModel]);
        setUnderlyingAssetPortfolios([...underlyingAssetPortfolios, newPortfolioModel]);
        setIsAllPortfoliosSelected(false);
        setSelectedPortfolio(newPortfolioModel);
        refreshPortfolioProfile(newPortfolioModel);
    }
    
    async function editSelectedPortfolio(portfolioModel: PortfolioAddOrUpdateModel): Promise<void>{
        if (!selectedPortfolio || isAllPortfoliosSelected) {
            return;
        }

        const previousPortfolioGroupId = selectedPortfolio.portfolioGroup.id;
        const nextPortfolioGroup = portfolioModel.portfolioGroup ?? selectedPortfolio.portfolioGroup;

        const updatedPortfolio = portfolioModelService.copyPortfolio(selectedPortfolio, {
            name: portfolioModel.name,
            description: portfolioModel.description,
            marginBuffer: portfolioModel.marginBuffer,
            inceptionDate: portfolioModel.inceptionDate,
            useValuationDate: portfolioModel.useValuationDate,
            valuationDate: portfolioModel.valuationDate,
            portfolioGroup: portfolioMappers.mapPortfolioGroupModelToModel(nextPortfolioGroup),
            modifiedAt: new Date(),
        });

        if (updatedPortfolio.portfolioSettings !== null) {
            updatedPortfolio.portfolioSettings.usePopupForStepChangeSelector =
                portfolioModel.usePopupForStepChangeSelector;
        }

        setSelectedPortfolio(updatedPortfolio);
        const wasSaved = await savePortfolio(updatedPortfolio);

        if (wasSaved) {
            setUnderlyingAssetPortfolios(underlyingAssetPortfolios.map(portfolio =>
                portfolio.id === updatedPortfolio.id
                    ? updatedPortfolio
                    : portfolio));
        }

        if (wasSaved && previousPortfolioGroupId !== nextPortfolioGroup.id) {
            await setSelectedPortfolioGroupByName(nextPortfolioGroup);
        }
    }

    async function cloneSelectedPortfolio(): Promise<void> {
        if (!marketDataDto || selectedPortfolio === null || isAllPortfoliosSelected) {
            return;
        }

        const initialPortfolio = selectedPortfolio;
        const newPortfolioModel = portfolioMappers.mapModelToModel(initialPortfolio);
        
        newPortfolioModel.name = getNewPortfolioName();
        newPortfolioModel.id = 0;
        newPortfolioModel.orders.map(x => x.externalId = 0);
        
        if (newPortfolioModel.portfolioSettings) {
            newPortfolioModel.portfolioSettings.comparedPortfolioId = initialPortfolio.id;
        }

        await savePortfolio(newPortfolioModel);
        
        setPortfolios([...portfolios, newPortfolioModel]);
        setUnderlyingAssetPortfolios([...underlyingAssetPortfolios, newPortfolioModel]);
        
        setIsAllPortfoliosSelected(false);
        setSelectedPortfolio(newPortfolioModel);
        refreshPortfolioProfile(newPortfolioModel);

        setSelectedComparedPortfolio(initialPortfolio);
        refreshComparedPortfolioProfile(newPortfolioModel, initialPortfolio);
        
        function getNewPortfolioName(): string {
            const template = newPortfolioModel.name += ' - Clone';
            let counter = 0;

            while (portfolios.findIndex(x => x.name === getPortfolioName()) !== -1) {
                counter += 1;
            }

            return getPortfolioName();
            
            function getPortfolioName() : string {
                return counter === 0
                    ? template
                    : template + counter;
            }
        }
    }
    
    async function saveSelectedPortfolio(): Promise<void> {
        if (!selectedPortfolio) {
            return;
        }

        if (isAllPortfoliosSelected) {
            await saveSelectedPortfolioGroupSettings();
            return;
        }

        await savePortfolio(selectedPortfolio);
    }

    async function fetchSelectedPortfolio(marketData?: MarketDataDto | null): Promise<PortfolioModel | null> {
        if (!isUserAuthenticated()) {
            navigate('/login');
            return null;
        }

        const effectiveMarketData = marketData ?? marketDataDto;

        if (!selectedPortfolio || selectedPortfolio.id === 0 || isAllPortfoliosSelected || !effectiveMarketData) {
            return null;
        }

        let reloadedPortfolio: PortfolioModel | null = null;

        const portfolioDto = await portfolioApi.getById(selectedPortfolio.id);

        if (portfolioDto === null) {
            return null;
        }

        const mappedPortfolio = portfolioMappers.map(
            portfolioDto,
            effectiveMarketData.OptionInfos,
            effectiveMarketData.UnderlyingPrice,
        );

        reloadedPortfolio = mappedPortfolio;
        setSelectedPortfolio(mappedPortfolio);
        setPortfolios(portfolios.map(portfolio =>
            portfolio.id === mappedPortfolio.id
                ? mappedPortfolio
                : portfolio));
        setUnderlyingAssetPortfolios(underlyingAssetPortfolios.map(portfolio =>
            portfolio.id === mappedPortfolio.id
                ? mappedPortfolio
                : portfolio));
        localStorage.setItem(LocalStorageConstants.SelectedPortfolioName, mappedPortfolio.name);
        refreshPortfolioProfile(mappedPortfolio);

        if (selectedComparedPortfolio !== null) {
            setComparedPortfolioProfile(comparedPortfolioProfile);
            refreshComparedPortfolioProfile(mappedPortfolio, selectedComparedPortfolio);
        }

        return reloadedPortfolio;
    }

    async function savePortfolio(portfolioModel: PortfolioModel): Promise<boolean> {
        if (portfolioModel.id === AllPortfoliosConstants.Id) {
            return false;
        }

        if (!isUserAuthenticated()) {
            navigate('/login');
            return false;
        }

        if (!marketDataDto) {
            return false;
        }
        
        const selectedPortfolioDto = portfolioMappers.mapToDto(portfolioModel);

        selectedPortfolioDto.CurrentBalance = portfolioCalculationService.balance
            .calculateAtCurrent(portfolioModel.orders, marketDataDto!.UnderlyingPrice!);
        selectedPortfolioDto.ExpirationBalance = portfolioCalculationService.balance
            .calculateAtExpiration(portfolioModel.orders, marketDataDto!.UnderlyingPrice!);

        if (selectedPortfolioDto.Id === 0) {
            const createdPortfolioId = await portfolioApi.create(selectedPortfolioDto);

            if (createdPortfolioId === null) {
                enqueueSnackbar("Unable to save portfolio changes. Please try saving again.", { variant: "error" });
                return false;
            }

            portfolioModel.id = createdPortfolioId;
            setSelectedPortfolio(portfolioModel);
            localStorage.setItem(LocalStorageConstants.SelectedPortfolioName, portfolioModel.name);
            return true;
        }

        const updatedPortfolioDto = await portfolioApi.update(selectedPortfolioDto);

        if (updatedPortfolioDto === null) {
            enqueueSnackbar("Unable to save portfolio changes. Please try saving again.", { variant: "error" });
            return false;
        }

        setSelectedPortfolio(portfolioMappers.map(
            updatedPortfolioDto,
            marketDataDto.OptionInfos,
            marketDataDto.UnderlyingPrice,
        ));
        localStorage.setItem(LocalStorageConstants.SelectedPortfolioName, updatedPortfolioDto.Name);
        return true;
    }

    async function saveSelectedPortfolioGroupSettings(): Promise<boolean> {
        if (!selectedPortfolio || !selectedPortfolioGroup || !underlyingAssetDto) {
            return false;
        }

        if (!isUserAuthenticated()) {
            navigate('/login');
            return false;
        }

        const portfolioGroupDto = {
            Id: selectedPortfolioGroup.id,
            Name: selectedPortfolioGroup.name,
            UnderlyingAsset: underlyingAssetDto,
            PortfolioSettings: portfolioMappers.mapSettingsModelToDto(selectedPortfolio.portfolioSettings),
        };

        const updatedPortfolioGroupDto = await portfolioGroupApi.update(portfolioGroupDto);

        if (updatedPortfolioGroupDto === null) {
            enqueueSnackbar("Unable to save portfolio changes. Please try saving again.", { variant: "error" });
            return false;
        }

        const updatedPortfolioGroup = portfolioMappers.mapPortfolioGroup(updatedPortfolioGroupDto);
        const updatedSelectedPortfolio = portfolioModelService.copyPortfolio(selectedPortfolio, {
            portfolioGroup: portfolioMappers.mapPortfolioGroupModelToModel(updatedPortfolioGroup),
            portfolioSettings: updatedPortfolioGroup.portfolioSettings !== null
                ? portfolioMappers.mapSettingsModelToModel(updatedPortfolioGroup.portfolioSettings)
                : null,
        });

        setPortfolioGroups(portfolioGroups.map(portfolioGroup =>
            portfolioGroup.id === updatedPortfolioGroup.id
                ? updatedPortfolioGroup
                : portfolioGroup));
        setSelectedPortfolioGroup(updatedPortfolioGroup);
        setSelectedPortfolio(updatedSelectedPortfolio);
        refreshPortfolioProfile(updatedSelectedPortfolio);

        const settings = updatedSelectedPortfolio.portfolioSettings;

        if (settings?.useComparedPortfolio && settings.comparedPortfolioId !== null && marketDataDto) {
            const comparedPortfolioDto = await getPortfolioById(settings.comparedPortfolioId);

            if (comparedPortfolioDto !== null) {
                const comparedPortfolioModel = portfolioMappers.map(
                    comparedPortfolioDto,
                    marketDataDto.OptionInfos,
                    marketDataDto.UnderlyingPrice,
                );

                setSelectedComparedPortfolio(comparedPortfolioModel);
                refreshComparedPortfolioProfile(updatedSelectedPortfolio, comparedPortfolioModel);
            }
        } else {
            setSelectedComparedPortfolio(null);
            setComparedPortfolioProfile({ data: [] });
        }

        return true;
    }

    async function getPortfolioById(id: number): Promise<PortfolioDto | null> {
        return await portfolioApi.getById(id);
    }

    async function saveCustomImpliedVolatility(
        positionsForUpdate: PositionForUpdate[],
        ordersForUpdate: OrderForUpdate[],
    ): Promise<void> {
        if (positionsForUpdate.length === 0 && ordersForUpdate.length === 0 ) {
            return;
        }
        
        if (!isUserAuthenticated()) {
            navigate('/login');
            return;
        }

        if (!selectedPortfolio || isAllPortfoliosSelected) {
            return;
        }
        
        await portfolioApi.saveCustomImpliedVolatility(
            selectedPortfolio.id,
            {
                positionsForUpdate,
                ordersForUpdate,
            },
        );
    }

    async function deleteSelectedPortfolio(forceDelete: boolean): Promise<void> {
        if (!isUserAuthenticated()) {
            navigate('/login');
            return;
        }
        
        if (!selectedPortfolio || isAllPortfoliosSelected) {
            return;
        }

        const deletedPortfolioIndex = portfolios
            .findIndex(x => x.name === selectedPortfolio.name);
        
        if (selectedPortfolio.id !== 0) {
            const wasDeleted = await portfolioApi.deleteById(selectedPortfolio.id, forceDelete);

            if (!wasDeleted) {
                return;
            }
        }

        const updatedPortfolios = portfolios.filter(x => x.name !== selectedPortfolio.name);
        setPortfolios(updatedPortfolios);
        setUnderlyingAssetPortfolios(underlyingAssetPortfolios.filter(x => x.name !== selectedPortfolio.name));

        if (updatedPortfolios.length > 0) {
            const nextSelectedPortfolio = updatedPortfolios[Math.max(deletedPortfolioIndex - 1, 0)];
            setSelectedPortfolio(nextSelectedPortfolio);
            localStorage.setItem(LocalStorageConstants.SelectedPortfolioName, nextSelectedPortfolio.name);
        } else {
            setSelectedPortfolio(null);
        }
    }

    return {
        marketDataDto,
        portfolioGroups,
        selectedPortfolioGroup,
        portfolios,
        underlyingAssetPortfolios,
        selectedPortfolio,
        isAllPortfoliosSelected,
        createPortfolioGroup,
        editSelectedPortfolioGroup,
        deleteSelectedPortfolioGroup,
        createPortfolio,
        portfolioProfile,
        comparedPortfolioProfile,
        setSelectedPortfolio,
        setSelectedPortfolioGroupByName,
        setSelectedPortfolioByName,
        setAllPortfoliosByUnderlyingAsset,
        editSelectedPortfolio,
        saveSelectedPortfolio,
        savePortfolio,
        fetchSelectedPortfolio,
        deleteSelectedPortfolio,
        refreshPortfolioProfile,
        selectedComparedPortfolio,
        setSelectedComparedPortfolioByName,
        refreshComparedPortfolioProfile,
        setComparedPortfolioProfile,
        cloneSelectedPortfolio,
        selectedPortfolioDates,
        saveCustomImpliedVolatility,
    };
}
